import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useSession } from '../session/SessionManager';
import { notificationService } from '../../services/api/notificationService';
import { ApiNotification } from '../../types/notification';
import { isApiError } from '../../services/api/client';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

// Keep existing local notification interface for toast system
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
  autoClose?: boolean;
}

interface NotificationContextType {
  // Toast notifications (local)
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  showSuccess: (title: string, message: string, duration?: number) => void;
  showError: (title: string, message: string, duration?: number) => void;
  showWarning: (title: string, message: string, duration?: number) => void;
  showInfo: (title: string, message: string, duration?: number) => void;
  
  // API notifications
  apiNotifications: ApiNotification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  createPriceAlert: (instrumentId: string, condition: 'above' | 'below', targetPrice: number) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const sessionData = useSession();
  
  // Toast notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  
  // API notifications state
  const [apiNotifications, setApiNotifications] = useState<ApiNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Toast notification methods
  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification: Notification = {
      ...notification,
      id,
      autoClose: notification.autoClose !== false,
      duration: notification.duration || 5000
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove notification
    if (newNotification.autoClose) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const showSuccess = useCallback((title: string, message: string, duration?: number) => {
    addNotification({ type: 'success', title, message, duration });
  }, [addNotification]);

  const showError = useCallback((title: string, message: string, duration?: number) => {
    addNotification({ type: 'error', title, message, duration });
  }, [addNotification]);

  const showWarning = useCallback((title: string, message: string, duration?: number) => {
    addNotification({ type: 'warning', title, message, duration });
  }, [addNotification]);

  const showInfo = useCallback((title: string, message: string, duration?: number) => {
    addNotification({ type: 'info', title, message, duration });
  }, [addNotification]);

  // API notification methods
  const refreshNotifications = useCallback(async () => {
    if (!sessionData?.currentAccount?.accountId) return;

    try {
      const response = await notificationService.getNotifications({
        limit: 50,
        page: 1
      });
      setApiNotifications(response.notifications);
      setUnreadCount(response.unreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      if (isApiError(error)) {
        showError('Notification Error', `Failed to load notifications: ${error.message}`);
      }
    }
  }, [sessionData?.currentAccount?.accountId, showError]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state
      setApiNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
      if (isApiError(error)) {
        showError('Notification Error', `Failed to mark as read: ${error.message}`);
      }
    }
  }, [showError]);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Update local state
      setApiNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      setUnreadCount(0);
      
      showSuccess('Success', 'All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      if (isApiError(error)) {
        showError('Notification Error', `Failed to mark all as read: ${error.message}`);
      }
    }
  }, [showSuccess, showError]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Update local state
      const deletedNotification = apiNotifications.find(n => n.id === notificationId);
      setApiNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
    } catch (error) {
      console.error('Error deleting notification:', error);
      if (isApiError(error)) {
        showError('Notification Error', `Failed to delete notification: ${error.message}`);
      }
    }
  }, [apiNotifications, showError]);

  const createPriceAlert = useCallback(async (instrumentId: string, condition: 'above' | 'below', targetPrice: number) => {
    try {
      await notificationService.createPriceAlert(instrumentId, condition, targetPrice);
      showSuccess('Price Alert Created', `Alert set for ${instrumentId} ${condition} ${targetPrice}`);
    } catch (error) {
      console.error('Error creating price alert:', error);
      if (isApiError(error)) {
        showError('Alert Error', `Failed to create price alert: ${error.message}`);
      }
    }
  }, [showSuccess, showError]);

  // Real-time notification subscription
  useEffect(() => {
    if (!sessionData?.currentAccount?.accountId) return;

    const handleRealTimeNotification = (notification: ApiNotification) => {
      // Add to API notifications list
      setApiNotifications(prev => [notification, ...prev]);
      
      // Update unread count if new notification is unread
      if (!notification.read) {
        setUnreadCount(prev => prev + 1);
      }

      // Show as toast based on notification type
      const toastType = getToastTypeFromApiNotification(notification.type);
      addNotification({
        type: toastType,
        title: notification.title,
        message: notification.message,
        duration: notification.type === 'position_expiring' ? 10000 : 5000 // Longer duration for critical notifications
      });
    };

    // Subscribe to real-time notifications
    notificationService.subscribeToNotifications(handleRealTimeNotification);

    return () => {
      notificationService.unsubscribeFromNotifications(handleRealTimeNotification);
    };
  }, [sessionData?.currentAccount?.accountId, addNotification]);

  // Initial load of notifications
  useEffect(() => {
    if (sessionData?.currentAccount?.accountId) {
      refreshNotifications();
    }
  }, [sessionData?.currentAccount?.accountId, refreshNotifications]);

  // Helper function to convert API notification type to toast type
  const getToastTypeFromApiNotification = (type: string): NotificationType => {
    switch (type) {
      case 'trade_result':
        return 'success';
      case 'position_expiring':
        return 'warning';
      case 'price_alert':
        return 'info';
      case 'system':
        return 'error';
      default:
        return 'info';
    }
  };

  const value: NotificationContextType = {
    // Toast notifications
    notifications,
    addNotification,
    removeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    
    // API notifications
    apiNotifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createPriceAlert,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationSystem notifications={notifications} onRemove={removeNotification} />
    </NotificationContext.Provider>
  );
};

interface NotificationSystemProps {
  notifications: Notification[];
  onRemove: (id: string) => void;
}

const NotificationSystem: React.FC<NotificationSystemProps> = ({ notifications, onRemove }) => {
  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return 'ℹ️';
    }
  };

  const getTypeClass = (type: NotificationType) => {
    switch (type) {
      case 'success': return 'toast-success';
      case 'error': return 'toast-error';
      case 'warning': return 'toast-warning';
      case 'info': return 'toast-info';
      default: return 'toast-info';
    }
  };

  return (
    <>
      <style>{`
        .toast-container {
          position: fixed;
          top: var(--spacing-20);
          right: var(--spacing-20);
          z-index: var(--z-toast);
          pointer-events: none;
        }
        
        .toast {
          background: var(--slate-high);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-toast);
          padding: var(--spacing-16) var(--spacing-20);
          margin-bottom: var(--spacing-12);
          max-width: 400px;
          cursor: pointer;
          animation: slideInWeb var(--transition-slow);
          pointer-events: auto;
          color: var(--text-primary);
        }
        
        .toast-success {
          border-color: var(--success);
          background: var(--slate-high);
        }
        
        .toast-error {
          border-color: var(--error);
          background: var(--slate-high);
        }
        
        .toast-warning {
          border-color: var(--warning);
          background: var(--slate-high);
        }
        
        .toast-info {
          border-color: var(--glacier-blue);
          background: var(--slate-high);
        }
        
        .toast-content {
          display: flex;
          align-items: flex-start;
          gap: var(--spacing-12);
        }
        
        .toast-icon {
          font-size: var(--icon-size-md);
          flex-shrink: 0;
        }
        
        .toast-text {
          flex: 1;
        }
        
        .toast-title {
          font-weight: var(--font-weight-semibold);
          font-size: var(--font-size-lg);
          margin-bottom: var(--spacing-4);
          line-height: var(--line-height-normal);
          color: var(--text-primary);
        }
        
        .toast-message {
          font-size: var(--font-size-md);
          opacity: 0.9;
          line-height: var(--line-height-normal);
          color: var(--text-secondary);
        }
        
        .toast-close {
          background: var(--slate-medium);
          border: none;
          color: var(--text-secondary);
          width: var(--icon-size-md);
          height: var(--icon-size-md);
          border-radius: var(--radius-sm);
          cursor: pointer;
          font-size: var(--font-size-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all var(--transition-fast);
        }
        
        .toast-close:hover {
          background: var(--slate-low);
          color: var(--text-primary);
        }
        
        @media (max-width: 768px) {
          .toast-container {
            top: var(--spacing-16);
            right: var(--spacing-16);
            left: var(--spacing-16);
          }
          
          .toast {
            animation: slideInMobile var(--transition-slow);
            max-width: none;
          }
        }
      `}</style>
      
      <div className="toast-container">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`toast ${getTypeClass(notification.type)}`}
            onClick={() => onRemove(notification.id)}
          >
            <div className="toast-content">
              <span className="toast-icon">
                {getIcon(notification.type)}
              </span>
              <div className="toast-text">
                <div className="toast-title">
                  {notification.title}
                </div>
                <div className="toast-message">
                  {notification.message}
                </div>
              </div>
              <button
                className="toast-close"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(notification.id);
                }}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default NotificationProvider;
export { NotificationSystem };
